import React, { useEffect, useState, useCallback } from "react";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { LedgerTable } from "@/components/v3/LedgerTable";
import api from "@/lib/api";
import { formatINRFull, formatCr, formatNumber } from "@/lib/format";
import {
  Search, ChevronLeft, ChevronRight, Download, BadgeCheck, Banknote, Layers, X,
  Building2, Mail, Phone, MapPin, FileText, Percent, Clock, Wallet, ShieldCheck,
} from "lucide-react";
import { Kpi } from "@/components/charts";
import { generateLoanFilePDF } from "@/lib/loanFilePdf";

function Select({ value, onChange, options, label, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="px-3 py-2.5 rounded-sm border border-slate-300 bg-white text-sm font-medium text-slate-700 focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition cursor-pointer">
      <option value="all">{label}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1">
        {Icon && <Icon size={13} />} {label}
      </div>
      <div className="text-sm font-semibold text-[#1a1a1b] break-words">{value || "—"}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rf-card p-5">
      <h3 className="font-display font-bold text-[#1a1a1b] mb-1">{title}</h3>
      <div className="grid grid-cols-2 gap-x-6">{children}</div>
    </div>
  );
}

function Badge({ ok, yes = "Yes", no = "No" }) {
  return <span className={`text-sm font-semibold ${ok ? "text-emerald-600" : "text-slate-400"}`}>{ok ? yes : no}</span>;
}

function DocLink({ name, url, signed }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="doc-link"
      className="flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-slate-200 hover:border-[#b3122a] hover:bg-[#b3122a]/5 transition text-sm">
      <span className="flex items-center gap-2 text-[#1a1a1b] font-medium truncate"><FileText size={14} className="text-[#b3122a] flex-shrink-0" />{name}</span>
      <span className="flex items-center gap-2 flex-shrink-0">
        {signed !== undefined && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${signed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{signed ? "SIGNED" : "UNSIGNED"}</span>}
        <Download size={14} className="text-slate-400" />
      </span>
    </a>
  );
}

function KV({ rows }) {
  return <div className="grid grid-cols-2 gap-x-6">{rows.map((r, i) => <Field key={i} label={r.label} value={r.value} />)}</div>;
}

function DynamicSection({ s }) {
  const empty =
    (s.type === "records" && !(s.records && s.records.length)) ||
    (s.type === "documents" && !(s.documents && s.documents.length)) ||
    (s.type === "table" && !(s.rows && s.rows.length)) ||
    (s.type === "kv" && !(s.rows && s.rows.length) && !(s.documents && s.documents.length));
  const meta = s.type === "records" ? `${(s.records || []).length} record(s)`
    : s.type === "documents" ? `${(s.documents || []).length} file(s)`
    : s.type === "table" ? `${(s.rows || []).length} row(s)` : "";
  return (
    <details className="rf-card !p-0 overflow-hidden group" data-testid={`section-${s.key}`} open={!empty && s.key !== "applicant"}>
      <summary className="cursor-pointer select-none px-5 py-4 flex items-center justify-between font-display font-bold text-[#1a1a1b]">
        <span>{s.title}{!empty && meta && <span className="ml-2 text-xs font-semibold text-slate-400">{meta}</span>}</span>
        <ChevronRight size={16} className="text-slate-400 group-open:rotate-90 transition-transform" />
      </summary>
      <div className="px-5 pb-5">
        {empty ? <p className="text-sm text-slate-400 italic" data-testid={`section-${s.key}-empty`}>Not available</p> : (
          <>
            {s.type === "kv" && <><KV rows={s.rows} />{s.documents && s.documents.length > 0 && <div className="grid sm:grid-cols-2 gap-2 mt-3">{s.documents.map((dd, i) => <DocLink key={i} name={dd.label} url={dd.url} />)}</div>}</>}
            {s.type === "documents" && <div className="grid sm:grid-cols-2 gap-2">{s.documents.map((dd, i) => <DocLink key={i} name={dd.name} url={dd.url} signed={dd.signed} />)}</div>}
            {s.type === "records" && <div className="space-y-4">{s.records.map((rec, i) => <div key={i} className="border border-slate-100 rounded-sm p-3"><div className="text-xs font-bold text-[#b3122a] mb-1">#{i + 1}</div><KV rows={rec.rows} />{rec.documents && rec.documents.length > 0 && <div className="grid sm:grid-cols-2 gap-2 mt-2">{rec.documents.map((dd, j) => <DocLink key={j} name={dd.label} url={dd.url} />)}</div>}</div>)}</div>}
            {s.type === "table" && <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="text-left text-slate-400 border-b border-slate-100">{s.columns.map((c) => <th key={c} className="py-2 px-2 whitespace-nowrap">{c}</th>)}</tr></thead><tbody>{s.rows.map((r, i) => <tr key={i} className="border-b border-slate-50">{s.columns.map((c) => <td key={c} className="py-1.5 px-2 whitespace-nowrap">{r[c]}</td>)}</tr>)}</tbody></table></div>}
          </>
        )}
      </div>
    </details>
  );
}

function DetailDrawer({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/approved/${id}`).then((r) => setD(r.data)).catch(() => {}); }, [id]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="approved-detail">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-[#F4F1EA] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#111111] text-white px-6 py-5 flex items-start justify-between z-10">
          <div>
            <div className="font-mono text-xs text-slate-400">Loan No: {d?.applicant_no || "…"}</div>
            <h2 className="font-display text-xl font-extrabold mt-1">{d?.company || "Loading…"}</h2>
            {d && <div className="text-sm text-slate-300 mt-1">{formatINRFull(d.loan_amount)} · {d.loan_type} · {d.tenor} mo · {d.app_type}</div>}
          </div>
          <div className="flex items-center gap-3">
            {d && (
              <button onClick={() => generateLoanFilePDF(d)} data-testid="download-loan-pdf"
                className="flex items-center gap-2 px-3.5 py-2 rounded-sm bg-[#b3122a] text-white text-xs font-semibold hover:bg-[#940f23] transition">
                <Download size={14} /> Download PDF
              </button>
            )}
            <button onClick={onClose} data-testid="close-detail" className="text-slate-300 hover:text-white"><X size={22} /></button>
          </div>
        </div>
        {!d ? <div className="p-8 text-slate-400">Loading…</div> : (
          <div className="p-6 space-y-5">
            <Section title="Loan Summary">
              <Field icon={Wallet} label="Applied Loan Amount" value={formatINRFull(d.loan_amount)} />
              <Field icon={Clock} label="Applied Tenor (Months)" value={d.tenor} />
              <Field icon={Layers} label="Loan Type" value={d.loan_type} />
              <Field icon={BadgeCheck} label="Application Type" value={d.app_type} />
              <Field icon={Wallet} label="MCC Sanctioned Amount" value={d.mcc_loan_amt ? formatINRFull(d.mcc_loan_amt) : "—"} />
              <Field icon={Clock} label="MCC Sanctioned Tenor" value={d.mcc_tenor || "—"} />
              <Field icon={Wallet} label="DD Sanctioned Amount" value={d.dd_loan_amt ? formatINRFull(d.dd_loan_amt) : "—"} />
              <Field icon={Clock} label="DD Sanctioned Tenor" value={d.dd_tenor || "—"} />
            </Section>

            <Section title="Company & KYC">
              <Field icon={Building2} label="Company Name" value={d.company} />
              <Field icon={FileText} label="Company Type" value={d.company_type} />
              <Field icon={FileText} label="PAN" value={d.pan} />
              <Field icon={FileText} label="GST No" value={d.gst} />
              <Field icon={FileText} label="CIN" value={d.cin} />
              <Field icon={Building2} label="Business Nature" value={d.business_nature} />
              <Field icon={Clock} label="Years in Business" value={d.years_in_business} />
              <Field icon={FileText} label="PAN Name Match" value={d.pan_name_match ? "Verified" : "—"} />
            </Section>

            <Section title="Contact & Address">
              <Field icon={Mail} label="Primary Email" value={d.email} />
              <Field icon={Mail} label="Secondary Email" value={d.secondary_email} />
              <Field icon={Phone} label="Phone" value={`${d.country_code || ""} ${d.phone}`.trim()} />
              <Field icon={Phone} label="Alternate Phone" value={d.alt_phone} />
              <Field icon={MapPin} label="State" value={d.state} />
              <Field icon={MapPin} label="Landmark" value={d.landmark} />
              <Field icon={MapPin} label="Address" value={d.address} />
            </Section>

            <Section title="Financials">
              <Field icon={Wallet} label="EMI Affordability" value={formatINRFull(d.emi_affordability)} />
              <Field icon={Banknote} label="Monthly Net Income" value={formatINRFull(d.monthly_net_income)} />
              <Field icon={Wallet} label="Company Equity" value={formatINRFull(d.company_equity)} />
              <Field icon={Wallet} label="Company Debt" value={formatINRFull(d.company_debt)} />
              <Field icon={Percent} label="NPA in last 3 yrs" value={d.npa_last_3y || "No"} />
            </Section>

            <Section title="Credit Assessment Workflow">
              <Field icon={ShieldCheck} label="Risk Officer (RO)" value={d.ro_status || "—"} />
              <Field icon={ShieldCheck} label="Credit Officer (CO)" value={d.co_status || "—"} />
              <Field icon={ShieldCheck} label="Credit Committee (MCC)" value={d.mcc_status || "—"} />
              <Field icon={ShieldCheck} label="Due Diligence (DD)" value={d.dd_status || "—"} />
              <div className="py-2.5 border-b border-slate-100">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1"><BadgeCheck size={13} /> Moderator Approved</div>
                <Badge ok={d.moderator_approved} />
              </div>
              <div className="py-2.5 border-b border-slate-100">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1"><BadgeCheck size={13} /> DD Moderator Approved</div>
                <Badge ok={d.dd_moderator_approved} />
              </div>
            </Section>

            <Section title="Documents & Timeline">
              <div className="py-2.5 border-b border-slate-100">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1"><FileText size={13} /> Term Sheet</div>
                <Badge ok={d.has_term_sheet} yes="Available" no="Not generated" />
              </div>
              <div className="py-2.5 border-b border-slate-100">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1"><FileText size={13} /> DD Report</div>
                <Badge ok={d.has_dd_report} yes="Available" no="Not generated" />
              </div>
              <Field icon={Clock} label="Created Date" value={d.created_date} />
              <Field icon={BadgeCheck} label="Approved Date" value={d.approved_date} />
              <Field icon={BadgeCheck} label="DD Approved Date" value={d.dd_approved_date} />
              <Field icon={Clock} label="Last Modified" value={d.modified_date} />
              <Field icon={FileText} label="Remark" value={d.remark} />
              <Field icon={FileText} label="Submissions" value={d.submitted_count} />
            </Section>

            <div className="pt-3">
              <h3 className="font-display text-lg font-extrabold text-[#1a1a1b]">Full Loan File</h3>
              <p className="text-xs text-slate-400 mb-1">All linked records & documents — live from the loan management system. Empty sections show "Not available".</p>
            </div>
            {d.sections && d.sections.map((s) => <DynamicSection key={s.key} s={s} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ApprovedLoans() {
  const [version] = useLiveRefresh();
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ loan_types: [] });
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [search, setSearch] = useState("");
  const [loanType, setLoanType] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/approved/summary").then((r) => setSummary(r.data)).catch((e) => setError(e.response?.data?.detail || e.message));
    api.get("/approved/filters").then((r) => setFilters(r.data)).catch(() => {});
  }, [version]);

  const params = { search, loan_type: loanType };
  const fetchList = useCallback(() => {
    setLoading(true);
    api.get("/approved/list", { params: { ...params, page, page_size: 20 } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search, loanType, page, version]);

  useEffect(() => { const t = setTimeout(fetchList, 300); return () => clearTimeout(t); }, [fetchList]);
  useEffect(() => { setPage(1); }, [search, loanType]);

  const exportCsv = async () => {
    const res = await api.get("/approved/export", { params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "approved_loans.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  if (error) return <div className="p-8 text-[#b3122a]" data-testid="approved-error">Failed to load: {error}</div>;

  const totalPages = Math.max(1, Math.ceil(data.total / 20));

  return (
    <div className="p-6 lg:p-8" data-testid="approved-page">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-[#1a1a1b]">Approved Loans</h1>
          <p className="text-slate-500 mt-1">{summary ? formatNumber(summary.total) : "…"} approved applications ready for disbursement</p>
        </div>
        <button onClick={exportCsv} data-testid="export-csv-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#b3122a] text-white text-sm font-semibold hover:bg-[#8c0d20] transition"><Download size={15} /> Export</button>
      </header>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Kpi icon={BadgeCheck} label="Approved Loans" value={formatNumber(summary.total)} sub="Awaiting / under disbursement" tone="green" delay={0} testid="kpi-approved" highlight />
          <Kpi icon={Banknote} label="Sanctioned Value" value={formatCr(summary.total_amount)} sub={`Avg ${formatCr(summary.avg_ticket)}`} tone="red" delay={60} testid="kpi-value" />
          <Kpi icon={Layers} label="Top Product" value={summary.by_type[0]?.name || "—"} sub={summary.by_type[0] ? `${summary.by_type[0].count} loans · ${formatCr(summary.by_type[0].value)}` : ""} tone="blue" delay={120} testid="kpi-product" />
        </div>
      )}

      <div className="rf-card p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="approved-search"
              placeholder="Name, Phone, Application No, Place…"
              className="w-full pl-10 pr-4 py-2.5 rounded-sm border border-slate-300 text-sm focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition" />
          </div>
          <Select value={loanType} onChange={setLoanType} options={filters.loan_types} label="All loan types" testid="filter-loantype" />
        </div>
      </div>

      <div className="rf-card overflow-hidden">
        <div className="overflow-x-auto">
          <LedgerTable labels={["Application no.", "Created / Approved", "Company", "Contact", "Loan details", "Application type"]} className="w-full text-sm" data-testid="approved-table">
            <thead>
              <tr className="bg-[#111111] text-slate-300">
                {["Application No", "Created / Approved", "Company", "Contact", "Loan Details", "App Type"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold text-xs uppercase tracking-wide text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="approved-empty">No approved loans match.</td></tr>}
              {!loading && data.items.map((l) => (
                <tr key={l.id} onClick={() => setDetailId(l.id)} className="rf-table-row border-t border-slate-100" data-testid={`approved-row-${l.applicant_no}`}>
                  <td className="px-4 py-3 font-mono text-xs text-[#b3122a] font-semibold whitespace-nowrap">{l.applicant_no}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">{l.created_date}<br /><span className="text-slate-400">{l.approved_date}</span></td>
                  <td className="px-4 py-3 font-semibold text-[#1a1a1b] max-w-[220px]">{l.company}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{l.email}<br />{l.phone}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatINRFull(l.loan_amount)}<br /><span className="text-xs text-slate-400">{l.tenor} mo · {l.loan_type}</span></td>
                  <td className="px-4 py-3"><span className="px-2.5 py-1 rounded-full text-xs font-semibold border border-slate-200 bg-slate-50 text-slate-600">{l.app_type}</span></td>
                </tr>
              ))}
            </tbody>
          </LedgerTable>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-sm text-slate-500">Page {data.page} of {totalPages} · {formatNumber(data.total)} approved</span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="page-prev" className="px-3 py-1.5 rounded-sm border border-slate-300 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition flex items-center gap-1"><ChevronLeft size={16} /> Prev</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="page-next" className="px-3 py-1.5 rounded-sm border border-slate-300 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition flex items-center gap-1">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      {detailId && <DetailDrawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
